
import { HTTPRequestType, PaginatedResponse } from 'src/services/api.types';
import { ICalendar, BusyInterval, ICalendarEvent, AvailabilityRule, AvailabilitySlot, BookableEventType, ScheduleMeetingDto, MakeAppointmentDto, GetMonthMetadataDto, GetAvailableSlotsDto, GetMyBusyIntervalsDto, CreateCalendarEventDto, UpdateCalendarEventDto, CreateAvailabilityRuleDto, UpdateAvailabilityRuleDto, CreateBookableEventTypeDto, UpdateBookableEventTypeDto } from 'src/services/calendar/calendar.types';

import { rootApi } from '../rootApi';

export const calendarApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({

    /* Calendar --------------------------------------------------------------- */
    getOrgCalendars: builder.query<ICalendar[], void>({
      query: () => ({
        url: '/calendars',
      }),
      providesTags: (result) => ['OrgCalendars'],
    }),
    getAccountCalendar: builder.query<ICalendar, void>({
      query: () => ({
        url: '/calendars/my-calendar',
      }),
      providesTags: [],
    }),
    getCalendarById: builder.query<ICalendar, string>({
      query: (id) => ({
        url: `/calendars/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: 'Calendar', id }],
    }),
    getMyBusyIntervals: builder.query<BusyInterval[], GetMyBusyIntervalsDto>({
      query: ({ date, timezone }) => ({
        url: '/calendars/my-calendar/busy',
        params: { date, timezone },
      }),
      providesTags: ['CalendarEvents'],
    }),

    /* Calendar events ------------------------------------------------------- */
    getCalendarEventsByMonth: builder.query<ICalendarEvent[], { calendarId: string; month: number; year: number; timezone: string }>({
      query: ({ calendarId, month, year, timezone }) => ({
        url: `/calendars/${calendarId}/events/by-month`,
        params: { month, year, timezone },
      }),
      providesTags: (result, error, { calendarId, month, year }) => [{ type: 'CalendarEvents', id: calendarId }],
    }),
    getCalendarEvents: builder.query<PaginatedResponse<ICalendarEvent>, { calendarId: string; page: number; per_page: number, date?: string, search_query?: string }>({
      query: ({ calendarId, page, per_page, date, search_query }) => ({
        url: `/calendars/${calendarId}/events`,
        params: {
          page,
          per_page,
          date,
          search_query
        },
      }),
      providesTags: (result, error, { calendarId }) => [{ type: 'CalendarEvents', id: calendarId }],
    }),
    getCalendarEventById: builder.query<ICalendarEvent, { eventId: string }>({
      query: ({ eventId }) => ({
        url: `/calendars/events/${eventId}`,
      }),
      providesTags: (result, error, { eventId }) => [{ type: 'CalendarEvent', id: eventId }],
    }),
    createCalendarEvent: builder.mutation<ICalendarEvent, { calendarId: string; data: CreateCalendarEventDto }>({
      query: ({ calendarId, data }) => ({
        url: `/calendars/${calendarId}/events`,
        method: HTTPRequestType.POST,
        data
      }),
      invalidatesTags: (result, error, { calendarId }) => [{ type: 'CalendarEvents', id: calendarId }],
    }),
    updateCalendarEvent: builder.mutation<ICalendarEvent, { eventId: string; data: UpdateCalendarEventDto }>({
      query: ({ eventId, data }) => ({
        url: `/calendars/events/${eventId}`,
        method: HTTPRequestType.PUT,
        data
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'CalendarEvent', id: eventId }],
    }),
    deleteCalendarEvent: builder.mutation<void, { calendarId: string; eventId: string }>({
      query: ({ calendarId, eventId }) => ({
        url: `/calendars/events/${eventId}`,
        method: HTTPRequestType.DELETE
      }),
      invalidatesTags: (result, error, { calendarId, eventId }) => [{ type: 'CalendarEvent', id: eventId }, { type: 'CalendarEvents', id: calendarId }],
    }),
    scheduleMeeting: builder.mutation<ICalendarEvent, ScheduleMeetingDto>({
      query: (data) => ({
        url: '/calendars/meetings/schedule',
        method: HTTPRequestType.POST,
        data
      }),
      // The event always lands on the caller's own calendar, and the backend posts a Meeting note to
      // each contact/candidate participant's profile, so refresh the notes feeds and threads too.
      invalidatesTags: (result, error, { add_bot }) => [
        'CalendarEvents',
        'NotesFeed',
        'Comments',
        ...(add_bot ? ['Meetings' as const] : []),
      ],
    }),


    /* Bookable event types ------------------------------------------------------- */
    getBookableEventType: builder.query<BookableEventType, string>({
      query: (eventTypeId) => ({
        url: `/calendars/event-scheduler/event-types/${eventTypeId}`,
      }),
      providesTags: (result, error, eventTypeId) => [{ type: 'BookableEventType', id: eventTypeId }],
    }),
    getBookableEventTypes: builder.query<PaginatedResponse<BookableEventType>, { page: number; per_page: number, search_query?: string }>({
      query: ({ page, per_page, search_query }) => ({
        url: `/calendars/event-scheduler/event-types`,
        params: {
          page,
          per_page,
          search_query
        },
      }),
      providesTags: (result, error, args) => ['BookableEventTypes'],
    }),
    createBookableEventType: builder.mutation<BookableEventType, { data: CreateBookableEventTypeDto }>({
      query: ({ data }) => ({
        url: `/calendars/event-scheduler/event-types`,
        method: HTTPRequestType.POST,
        data
      }),
      invalidatesTags: ['BookableEventTypes'],
    }),
    updateBookableEventTypeById: builder.mutation<BookableEventType, { eventTypeId: string, data: UpdateBookableEventTypeDto }>({
      query: ({ eventTypeId, data }) => ({
        url: `/calendars/event-scheduler/event-types/${eventTypeId}`,
        method: HTTPRequestType.PUT,
        data
      }),
      invalidatesTags: (result, error, { eventTypeId }) => [{ type: 'BookableEventType', id: eventTypeId }, 'BookableEventTypes'],
    }),
    removeBookableEventTypeById: builder.mutation<void, { eventTypeId: string }>({
      query: ({ eventTypeId }) => ({
        url: `/calendars/event-scheduler/event-types/${eventTypeId}`,
        method: HTTPRequestType.DELETE
      }),
      invalidatesTags: (result, error, { eventTypeId }) => ['BookableEventTypes'],
    }),
    recordBookableEventView: builder.mutation<void, { eventTypeId: string }>({
      query: ({ eventTypeId }) => ({
        url: `/calendars/event-scheduler/event-types/${eventTypeId}/views`,
        method: HTTPRequestType.POST
      })
    }),
    makeAppointment: builder.mutation<{ link: string }, { eventTypeId: string; data: MakeAppointmentDto }>({
      query: ({ eventTypeId, data }) => ({
        url: `/calendars/event-scheduler/event-types/${eventTypeId}/appointments`,
        method: HTTPRequestType.POST,
        data
      })
    }),

    /* Availability rules ------------------------------------------------------- */
    getAvailabilityRules: builder.query<PaginatedResponse<AvailabilityRule>, { page: number; per_page: number, eventTypeId: string }>({
      query: ({ page, per_page, eventTypeId }) => ({
        url: `/calendars/event-scheduler/event-types/${eventTypeId}/availability/rules`,
        params: {
          page,
          per_page
        },
      }),
      providesTags: (result, error, { eventTypeId }) => [{ type: 'AvailabilityRules', id: eventTypeId }],
    }),

    createAvailabilityRule: builder.mutation<AvailabilityRule, { eventTypeId: string, data: CreateAvailabilityRuleDto }>({
      query: ({ eventTypeId, data }) => ({
        url: `/calendars/event-scheduler/event-types/${eventTypeId}/availability/rules`,
        method: HTTPRequestType.POST,
        data
      }),
      invalidatesTags: (result, error, { eventTypeId }) => [{ type: 'AvailabilityRules', id: eventTypeId }],
    }),

    updateAvailabilityRuleById: builder.mutation<AvailabilityRule, { eventTypeId: string, ruleId: string; data: UpdateAvailabilityRuleDto }>({
      query: ({ eventTypeId, ruleId, data }) => ({
        url: `/calendars/event-scheduler/event-types/${eventTypeId}/availability/rules/${ruleId}`,
        method: HTTPRequestType.PUT,
        data
      }),
      invalidatesTags: (result, error, { ruleId, eventTypeId }) => [{ type: 'AvailabilityRule', id: ruleId }, { type: 'AvailabilityRules', id: eventTypeId }],
    }),

    removeAvailabilityRuleById: builder.mutation<void, { eventTypeId: string, ruleId: string }>({
      query: ({ ruleId, eventTypeId }) => ({
        url: `/calendars/event-scheduler/event-types/${eventTypeId}/availability/rules/${ruleId}`,
        method: HTTPRequestType.DELETE
      }),
      invalidatesTags: (result, error, { ruleId, eventTypeId }) => [{ type: 'AvailabilityRule', id: ruleId }, { type: 'AvailabilityRules', id: eventTypeId }],
    }),

    /* Availability slots ------------------------------------------------------- */
    getAvailabilitySlots: builder.query<AvailabilitySlot[], { eventTypeId: string, data: GetAvailableSlotsDto }>({
      query: ({ eventTypeId, data }) => ({
        url: `/calendars/event-scheduler/event-types/${eventTypeId}/availability/slots`,
        method: HTTPRequestType.POST,
        data,
      })
    }),
    getMonthMetadata: builder.query<Record<string, boolean>, { eventTypeId: string, data: GetMonthMetadataDto }>({
      query: ({ eventTypeId, data }) => ({
        url: `/calendars/event-scheduler/event-types/${eventTypeId}/availability/month-metadata`,
        method: HTTPRequestType.POST,
        data,
      })
    }),

  })
});

export const {
  useGetOrgCalendarsQuery,
  useGetAccountCalendarQuery,
  useGetCalendarByIdQuery,
  useGetMyBusyIntervalsQuery,
  useGetCalendarEventsQuery,
  useGetCalendarEventsByMonthQuery,
  useLazyGetCalendarEventsByMonthQuery,
  useGetCalendarEventByIdQuery,
  useCreateCalendarEventMutation,
  useUpdateCalendarEventMutation,
  useDeleteCalendarEventMutation,
  useScheduleMeetingMutation,

  useGetBookableEventTypeQuery,
  useGetBookableEventTypesQuery,
  useCreateBookableEventTypeMutation,
  useUpdateBookableEventTypeByIdMutation,
  useRemoveBookableEventTypeByIdMutation,
  useRecordBookableEventViewMutation,
  useMakeAppointmentMutation,

  useGetAvailabilityRulesQuery,
  useCreateAvailabilityRuleMutation,
  useUpdateAvailabilityRuleByIdMutation,
  useRemoveAvailabilityRuleByIdMutation,
  useGetAvailabilitySlotsQuery,
  useGetMonthMetadataQuery,
} = calendarApi;




